import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import {
  POST_SELECTED_CHEF,
  POST_SELECTED_WHEN,
  POST_SELECTED_ZIP,
  POST_TO_CART,
  POST_USER_DATA
} from "../../store/action";
import { t } from "../../translations";
import { GET, POST } from "../constants/request-type";
import { service_api } from "../constants/url";
import { MakeRequestAsync } from "../functions/axios";
import { decryptSingleData } from "../functions/cryptojs";
import { dayPlusOne } from "../functions/dateFunctions";
import "../styles/layout.css";

// image importation

import SectionCart from "../elements/section-cart";

import { getStoredUserData } from "../../utils/authStorageUtils";
import { configureCrisp } from "../../utils/crispConfig";
import { createLogger } from "../../utils/logger";
import { getRefreshToken } from "../../utils/tokenUtils";
import CheckoutForm from "../elements/checkoutForm";

const logger = createLogger("LayoutCart");
const LayoutCart = () => {

  const location = useLocation();
  const history = useHistory();
  const searchParams = new URLSearchParams(location.search);
  const selectedChefId = searchParams.get("chef");

  const [checkoutPage, setCheckoutPage] = useState(false);

  const [deliveryDetail, setDeliveryDetail] = useState(0);
  const [selectedTip, setSelectedTip] = useState(null);

  const [province, setProvince] = useState(null);

  const [promo_code, setPromoCode] = useState("");
  const [promo_value, setPromoValue] = useState(0);
  const [payment_details, setPaymentDetails] = useState({
    digits: "",
    cvv: "",
    exp: "",
    zip: ""
  });

  // Removed tips state - no longer needed with new tip system

  const [orderId, setOrderId] = useState(null);
  const [custom_help, setCustomHelp] = useState(null);
  const [distanceInKM, setDistanceInKM] = useState(0);
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [load, setLoad] = useState(false);

  let selectedWhen = useSelector((state) => state.selectedWhen);
  let selectedLang = useSelector((state) => state.lang);
  let selectedZip = useSelector((state) => state.selectedZip);
  let selectedChef = useSelector((state) => state.selectedChef);

  let totalWithPromo = useSelector((state) => state.cartTotals?.totalWithPromo);

  let userData = useSelector((state) => state.user);

  // let setting = useSelector((state) => state.setting);

  // Debug: Monitor selectedChef changes in Redux
  useEffect(() => {
    if (selectedChef) {
      logger.log("📊 REDUX SELECTED CHEF UPDATED:", {
        reduxChef: {
          id: selectedChef._id,
          name: `${selectedChef.firstname} ${selectedChef.lastname}`,
          hasSelectedHub: !!selectedChef.selectedHub,
          selectedHub: selectedChef.selectedHub,
          selectedHubType: typeof selectedChef.selectedHub,
          isRestaurant: selectedChef.restaurant,
          isChef: selectedChef.chef,
          allKeys: Object.keys(selectedChef)
        },
        fullReduxChefData: selectedChef
      });

      if (selectedChef.selectedHub) {
        logger.log("🏢 REDUX CHEF HUB DATA:", selectedChef.selectedHub);
      } else {
        logger.warn("⚠️ REDUX CHEF HAS NO SELECTED HUB:", {
          chefId: selectedChef._id,
          chefName: `${selectedChef.firstname} ${selectedChef.lastname}`
        });
      }
    } else {
      logger.log("📊 REDUX SELECTED CHEF IS NULL/UNDEFINED");
    }
  }, [selectedChef]);

  const dispatch = useDispatch();

  useEffect(() => {
    const loadChefAndInitialData = async () => {
      try {
        setLoad(true);
        let currentChef = selectedChef;

        // 1. Fetch Chef Data if URL ID doesn't match Redux
        const isObjectId = /^[0-9a-fA-F]{24}$/.test(selectedChefId);
        let fetchedChef = null;

        if (isObjectId) {
          const response = await MakeRequestAsync({
            type: GET,
            url: service_api,
            route: `user/get-chef-user/${selectedChefId}`,
            token: userData?.refresh_token || null
          });
          fetchedChef = decryptSingleData(response.data.user);
        } else {
          // Name search fallback
          const response = await MakeRequestAsync({
            type: GET,
            url: service_api,
            route: "user/get-all-culinarian"
          });
          const chefs = decryptSingleData(response.data.user);
          const searchName = decodeURIComponent(selectedChefId).toLowerCase().trim();
          fetchedChef = chefs.find(c => 
            `${c.firstname} ${c.lastname}`.toLowerCase().includes(searchName)
          );
        }

        if (fetchedChef) {
          const storedData = getStoredUserData();
          currentChef = {
            ...fetchedChef,
            ...(storedData?.selectedHub && !fetchedChef.selectedHub ? { selectedHub: storedData.selectedHub } : {})
          };
          whichZip(currentChef.zip);
          whichChef(currentChef);
        } else if (!selectedChef) {
          history.push("/");
          return;
        }

        // 2. Sync Date and Fetch Province
        const tday = dayPlusOne();
        if (selectedWhen === null || selectedWhen < tday) WhenDate(tday);

        const response_province = await MakeRequestAsync({
          type: GET,
          url: service_api,
          route: `province/get-by-name/${currentChef?.province || "British Columbia"}`
        });
        setProvince(decryptSingleData(response_province.data.province));

      } catch (err) {
        logger.error("Error in loadChefAndInitialData:", err);
      } finally {
        setLoad(false);
      }
    };

    loadChefAndInitialData();
  }, [selectedChefId]);

  const getDeliveryFee = async () => {
    logger.log(t("debug.running", selectedLang));
    
    // Get refresh token if user is authenticated
    const refreshToken = userData?._id ? await getRefreshToken(userData, post_user_data) : null;
    
    const user_position = {
      latitude: userData?.address[deliveryDetail]?.lat,
      longitude: userData?.address[deliveryDetail]?.lng
    };

    // Debug: Log delivery fee calculation context
    logger.log("💰 Delivery fee calculation debug:", {
      isAuthenticated: !!refreshToken,
      isRestaurant: selectedChef?.restaurant,
      hasSelectedHub: !!selectedChef?.selectedHub,
      selectedHub: selectedChef?.selectedHub,
      chefId: selectedChef?._id,
      userPosition: user_position
    });

    // Prepare data based on whether it's a restaurant or chef
    let requestData;
    
    if (selectedChef?.restaurant) {
      // For restaurants, use chef_id directly
      requestData = {
        user_position: user_position,
        chef_id: selectedChef?._id
      };
      logger.log("💰 Using restaurant mode:", requestData);
    } else {
      // For chefs, use hub information if available
      if (selectedChef?.selectedHub) {
        requestData = {
          user_position: user_position,
          hub_id: selectedChef.selectedHub._id,
          chef_id: selectedChef?._id // Include chef_id for context
        };
        logger.log("💰 Using hub mode:", requestData);
      } else {
        // If no selectedHub available for a chef, warn and use fallback
        logger.warn("⚠️ Chef has no selectedHub for delivery fee calculation:", {
          chefId: selectedChef?._id,
          chefName: `${selectedChef?.firstname} ${selectedChef?.lastname}`
        });
        
        requestData = {
          user_position: user_position,
          chef_id: selectedChef?._id
        };
        logger.log("💰 Using fallback mode (no hub - chef may need hub assignment):", requestData);
      }
    }

    const request_details = {
      type: POST,
      url: service_api,
      route: "order/get-delivery-fee",
      data: requestData,
      token: refreshToken
    };

    try {
      const response = await MakeRequestAsync(request_details);
      // Backend now returns an object with distanceInKm and deliveryFee
      if (response.data.data) {
        setDistanceInKM(response.data.data.distanceInKm || 0);
        setDeliveryFee(response.data.data.deliveryFee || 0);
      }
    } catch (err) {
      logger.log(err);
    }
  };

  useEffect(() => {
    configureCrisp({
      autoload: true,
      lockMaximized: false,
      lockFullview: false
    });

    logger.log("=== CART PAGE DEBUG ===");
    logger.log(
      t("debug.cart_loaded", selectedLang),
      selectedChef,
      "selectedChefId:",
      selectedChefId
    );
    logger.log(t("debug.url_params", selectedLang), location.search);
    logger.log(
      t("debug.decoded_chef", selectedLang),
      decodeURIComponent(selectedChefId || "")
    );
    logger.log("selectedZip:", selectedZip);
    logger.log("selectedLang:", selectedLang);
    logger.log("userData:", userData);
    logger.log("checkoutPage state:", checkoutPage);
  }, [
    selectedChef,
    selectedChefId,
    history,
    location.search,
    selectedLang,
    selectedZip
    // Removed userData from dependencies to prevent re-running when AddressModal updates userData
  ]);

  useEffect(() => {
    if (userData && userData?.address?.length > 0) {
      // get delivery fee
      getDeliveryFee();
    }
  }, [deliveryDetail, getDeliveryFee, userData, selectedChef?.selectedHub, selectedChef?.restaurant]);

  const WhenDate = (value) => dispatch(post_selected_when(value));

  const post_to_cart = (data) => {
    dispatch(post_cart(data));
  };

  const post_user_data = (data) => {
    dispatch(post_user(data));
  };

  const whichChef = (value) => {
    logger.log("🚀 DISPATCHING CHEF TO REDUX:", {
      dispatchingChef: {
        id: value?._id,
        name: `${value?.firstname} ${value?.lastname}`,
        hasSelectedHub: !!value?.selectedHub,
        selectedHub: value?.selectedHub,
        selectedHubType: typeof value?.selectedHub,
        isRestaurant: value?.restaurant,
        isChef: value?.chef
      },
      fullChefData: value
    });
    dispatch(post_selected_chef(value));
  };
  const whichZip = (value) => dispatch(post_selected_zip(value));

  return (
    <div className="global-block">
      <div className="full-block">
        {/* Tu dois ajouter tout les autre compasant du projet ici */}
        {checkoutPage ? (
          <CheckoutForm
            clear_cart={() => {
              post_to_cart([]);
            }}
            order_id={orderId}
            promo_code={promo_code}
            promo_value={promo_value}
            delivery_address={deliveryDetail}
            setCheckoutPage={setCheckoutPage}
            amount={totalWithPromo}
          />
        ) : (
          <SectionCart
            deliveryDetail={deliveryDetail}
            setDeliveryDetail={setDeliveryDetail}
            distanceInKM={distanceInKM}
            deliveryFee={deliveryFee}
            selectedTip={selectedTip}
            setSelectedTip={setSelectedTip}
            payment_details={payment_details}
            setPaymentDetails={setPaymentDetails}
            promo_code={promo_code}
            province_detail={province}
            setPromoCode={setPromoCode}
            promo_value={promo_value}
            setPromoValue={setPromoValue}
            setCheckoutPage={setCheckoutPage}
            setOrderId={setOrderId}
            setCustomHelp={setCustomHelp}
            custom_help={custom_help}
            tips={[]}
          />
        )}

        <div
          style={{
            height: 40
          }}
        ></div>
      </div>
    </div>
  );
};

export default LayoutCart;

const post_selected_when = (data) => ({
  type: POST_SELECTED_WHEN,
  data: data
});

const post_selected_zip = (data) => ({
  type: POST_SELECTED_ZIP,
  data: data
});

const post_cart = (data) => ({
  type: POST_TO_CART,
  data: data
});

const post_selected_chef = (data) => ({
  type: POST_SELECTED_CHEF,
  data: data
});

const post_user = (data) => ({
  type: POST_USER_DATA,
  data: data
});
